import { supabase } from "../supabaseClient";
import { logger } from "../utils/logger";

const THUMBNAIL_BUCKET = "wishlist-thumbnails";
const THUMBNAIL_PREFIX = `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/${THUMBNAIL_BUCKET}/`;

const isOwnedThumbnail = (url) => !!url && url.startsWith(THUMBNAIL_PREFIX);
const extractThumbnailPath = (url) => url.slice(THUMBNAIL_PREFIX.length);
// pathPrefix is either a wishlist item's nameId (`${nameId}/...`) or the literal string
// "surprise" for claimedItemsService's Surprise Item thumbnails (`surprise/...`).
const buildThumbnailPath = (pathPrefix, userId) =>
  `${pathPrefix}/${userId}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}.jpg`;

const ITEM_SELECT = `
  id,
  name_id,
  item_text,
  url,
  price,
  thumbnail_url,
  created_at,
  sort_order,
  claimed_by,
  claimed_at,
  purchased_at,
  claimed_for_child_name,
  surprise_recipient_name_id,
  users:claimed_by ( display_name )
`;

export async function fetchItems(nameId, isOwner) {
  // The owner must never see surprise items bought for them, no exceptions — only
  // their own name_id matches. Everyone else also sees rows surprised in for this
  // name, but only if the surprise's adder left it visible to the group
  // (surprise_visible_to_group) — see SurpriseItemDialog.js's checkbox.
  let query = supabase.from("wishlist_items").select(ITEM_SELECT);
  query = isOwner
    ? query.eq("name_id", nameId)
    : query.or(`name_id.eq.${nameId},and(surprise_recipient_name_id.eq.${nameId},surprise_visible_to_group.eq.true)`);

  const { data, error } = await query
    // Same ordering the on-screen ranking uses, with created_at as a tiebreaker
    // for equal/null sort_order — see Item Ranking in docs/architecture-deep-dive.md.
    // Surprise items always have a null sort_order, so they naturally sort last.
    .order("sort_order", { ascending: true, nullsFirst: false })
    .order("created_at", { ascending: false });

  if (error) {
    logger.error('wishlistItemsService', 'fetch_items_failed', { nameId, isOwner, error: error.message });
    throw error;
  }
  return data || [];
}

export async function uploadThumbnail(file, pathPrefix, userId, context = {}) {
  const path = buildThumbnailPath(pathPrefix, userId);
  const { error } = await supabase.storage
    .from(THUMBNAIL_BUCKET)
    .upload(path, file, { contentType: "image/jpeg" });

  if (error) {
    logger.error('wishlistItemsService', 'thumbnail_upload_failed', { ...context, pathPrefix, error: error.message });
    throw error;
  }
  logger.info('wishlistItemsService', 'thumbnail_uploaded', { ...context, path });
  return supabase.storage.from(THUMBNAIL_BUCKET).getPublicUrl(path).data.publicUrl;
}

export async function deleteThumbnail(url) {
  if (!isOwnedThumbnail(url)) return;
  const path = extractThumbnailPath(url);
  const { error } = await supabase.storage.from(THUMBNAIL_BUCKET).remove([path]);

  if (error) {
    logger.warn('wishlistItemsService', 'thumbnail_delete_failed', { path, error: error.message });
    return;
  }
  logger.info('wishlistItemsService', 'thumbnail_deleted', { path });
}

export async function addItem({ nameId, userId, itemText, url, price, thumbnailUrl, sortOrder }) {
  const { data, error } = await supabase
    .from("wishlist_items")
    .insert({
      name_id: nameId,
      item_text: itemText,
      url: url || null,
      price,
      user_id: userId,
      thumbnail_url: thumbnailUrl,
      sort_order: sortOrder,
    })
    .select(ITEM_SELECT)
    .single();

  if (error) {
    logger.error('wishlistItemsService', 'add_item_failed', { itemText, nameId, error: error.message });
    throw error;
  }
  logger.info('wishlistItemsService', 'item_added', { itemText, nameId, hasThumbnail: !!thumbnailUrl, sortOrder });
  return data;
}

export async function updateItem({ id, itemText, url, price, thumbnailUrl }) {
  const { data, error } = await supabase
    .from("wishlist_items")
    .update({ item_text: itemText, url: url || null, price, thumbnail_url: thumbnailUrl })
    .eq("id", id)
    .select(ITEM_SELECT)
    .single();

  if (error) {
    logger.error('wishlistItemsService', 'update_item_failed', { itemId: id, error: error.message });
    throw error;
  }
  logger.info('wishlistItemsService', 'item_edited', { itemId: id });
  return data;
}

export async function deleteItem({ id }) {
  const { error } = await supabase.from("wishlist_items").delete().eq("id", id);

  if (error) {
    logger.error('wishlistItemsService', 'delete_item_failed', { itemId: id, error: error.message });
    throw error;
  }
  logger.info('wishlistItemsService', 'item_deleted', { itemId: id });
}

export async function claimItem({ itemId, userId, claimedForChildName = null }) {
  const { data, error } = await supabase
    .from("wishlist_items")
    .update({
      claimed_by: userId,
      claimed_at: new Date().toISOString(),
      claimed_for_child_name: claimedForChildName,
    })
    .eq("id", itemId)
    .select(ITEM_SELECT)
    .single();

  if (error) {
    logger.error('wishlistItemsService', 'claim_item_failed', { itemId, error: error.message });
    throw error;
  }
  logger.info('wishlistItemsService', 'item_claimed', { itemId, claimedForChild: claimedForChildName });
  return data;
}

export async function previewAmazonImport(url, existingItemTexts = []) {
  const { data, error } = await supabase.functions.invoke("amazon-import", { body: { url } });

  if (error) {
    let message = error?.message || "Failed to fetch items from Amazon.";
    try {
      const body = await error.context?.json?.();
      if (body?.error) message = body.error;
    } catch {
      // ignore — fall back to the message already set above
    }
    logger.error('wishlistItemsService', 'amazon_preview_failed', { url, error: message });
    throw new Error(message);
  }
  if (data?.error) {
    logger.error('wishlistItemsService', 'amazon_preview_failed', { url, error: data.error });
    throw new Error(data.error);
  }

  const lowerExisting = existingItemTexts.map((t) => t.toLowerCase().trim());
  return data.items.map((item) => {
    const isDuplicate = lowerExisting.some(
      (t) =>
        t.includes(item.title.toLowerCase().substring(0, 20)) ||
        item.title.toLowerCase().includes(t.substring(0, 20))
    );
    return { ...item, isDuplicate };
  });
}

export async function bulkImportItems(nameId, itemsToInsert) {
  if (itemsToInsert.length === 0) return;

  const { error } = await supabase.from("wishlist_items").insert(itemsToInsert);

  if (error) {
    logger.error('wishlistItemsService', 'bulk_import_failed', { nameId, count: itemsToInsert.length, error: error.message });
    throw error;
  }
  logger.info('wishlistItemsService', 'amazon_import', { nameId, count: itemsToInsert.length });
}

export async function reorderItems({ nameId, updates, order }) {
  const { error } = await supabase.rpc("reorder_wishlist_items", { p_updates: updates });

  if (error) {
    logger.error('wishlistItemsService', 'reorder_failed', { nameId, error: error.message });
    throw error;
  }
  logger.info('wishlistItemsService', 'items_reordered', { nameId, order });
}

export function subscribeToItemUpdates(nameId, onChange) {
  const channel = supabase
    .channel(`items-${nameId}`)
    .on("broadcast", { event: "items-updated" }, onChange)
    .subscribe();

  return () => supabase.removeChannel(channel);
}

export function notifyItemsUpdated(nameId) {
  const topic = `items-${nameId}`;

  // supabase.channel() reuses an existing channel object for a topic that's
  // already joined (e.g. this tab's own subscribeToItemUpdates listener).
  // Calling subscribe() on an already-joined channel is a no-op, so the
  // broadcast would silently never send. Send directly on it instead.
  const existing = supabase.getChannels().find((c) => c.topic === `realtime:${topic}`);
  if (existing && existing.state === "joined") {
    existing.send({ type: "broadcast", event: "items-updated" });
    return;
  }

  const channel = supabase.channel(topic);
  channel.subscribe((status) => {
    if (status === "SUBSCRIBED") {
      channel.send({ type: "broadcast", event: "items-updated" });
      supabase.removeChannel(channel);
    }
  });
}
